import { useState } from "react";
import { Link } from "react-router-dom";
import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Chip,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Tooltip,
} from "@heroui/react";
import {
    LuPencil,
    LuTrash,
    LuPlus,
    LuRefreshCw,
    LuEye,
} from "react-icons/lu";
import { useDataTable, useFetchData } from "@/hooks";
import {
    ActiveFilterChips,
    DataTableControlBar,
    DataTableSkeleton,
    DataTablePagination,
    DeleteConfirmModal,
    FilterButton,
    ChangeStatusModal,
} from "@/components/data-table";
import {
    EmptyState,
    ErrorState,
    ThemedText,
    PageHeader,
} from "@/components/ui";
import { FilterData } from "./components";
import { formatCurrency, formatDate } from "@/utils";
import { useUser } from "@/context/UserContext";

// Initial filter values
const initialFilters = {
    order_number: "",
    supplier_id: "",
    payment_status_id: "",
    payment_method_id: "",
    date_from: "",
    date_to: "",
};

// Skeleton columns for loading state
const skeletonColumns = [
    { name: "No.", skeletonWidth: "w-12" },
    { name: "No. Order", skeletonWidth: "w-32" },
    { name: "Supplier", skeletonWidth: "w-40" },
    { name: "Tanggal", skeletonWidth: "w-28" },
    { name: "Total", skeletonWidth: "w-32", align: "end" },
    { name: "Status Pembayaran", skeletonWidth: "w-24" },
    { name: "Status Order", skeletonWidth: "w-24" },
    { name: "Aksi", skeletonWidth: "w-20", align: "end" },
];

const PurchaseOrderData = () => {
    const { hasPermission } = useUser();
    const [orderStatusModal, setOrderStatusModal] = useState({
        isOpen: false,
        item: null,
    });

    const table = useDataTable(
        "purchase-orders/data",
        "purchase-orders/{id}/delete",
        {},
        initialFilters,
    );

    // Fetch filter options for label lookups
    const { data: suppliers } = useFetchData("select/supplier");
    const { data: statuses } = useFetchData("select/payment-status");
    const { data: paymentMethods } = useFetchData("select/payment-method");

    // Filter options to pass to FilterData component (handle null with || [])
    const filterOptions = {
        suppliers: suppliers || [],
        statuses: statuses || [],
        paymentMethods: paymentMethods || [],
    };

    // Filter labels with dynamic getValueLabel
    const filterLabels = {
        order_number: { label: "No. Order" },
        supplier_id: {
            label: "Supplier",
            getValueLabel: (val) => {
                const item = suppliers?.find(
                    (s) => String(s.value) === String(val),
                );
                return item?.title || val;
            },
        },
        payment_status_id: {
            label: "Status Pembayaran",
            getValueLabel: (val) => {
                const item = statuses?.find(
                    (s) => String(s.value) === String(val),
                );
                return item?.title || val;
            },
        },
        payment_method_id: {
            label: "Metode Bayar",
            getValueLabel: (val) => {
                const item = paymentMethods?.find(
                    (p) => String(p.value) === String(val),
                );
                return item?.title || val;
            },
        },
        date_from: { label: "Dari Tanggal" },
        date_to: { label: "Sampai Tanggal" },
    };

    // Handle remove single filter
    const handleRemoveFilter = (key) => {
        table.filterPanel.setPending((prev) => ({
            ...prev,
            [key]: initialFilters[key],
        }));
        table.filters.setFilter(key, initialFilters[key]);
    };

    if (table.error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Data Pembelian"
                    message={table.error?.message}
                    onRetry={table.refetch}
                />
            </div>
        );
    }

    return (
        <div className="flex h-full w-full flex-col gap-4">
            {/* Page Header */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Pembelian"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Pembelian" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <FilterButton
                        isOpen={table.filterPanel.isOpen}
                        onToggle={() =>
                            table.filterPanel.setIsOpen(
                                !table.filterPanel.isOpen,
                            )
                        }
                        activeCount={table.filters.activeCount}
                    />
                    {hasPermission("purchase-order.create") && (
                        <Button
                            as={Link}
                            to="/purchase-order/create"
                            color="primary"
                            startContent={<LuPlus />}
                            size="sm"
                        >
                            Tambah Order
                        </Button>
                    )}
                </div>
            </div>

            {/* Filter Panel */}
            <FilterData
                table={table}
                initialFilters={initialFilters}
                filterOptions={filterOptions}
            />

            {/* Active Filter Chips */}
            <ActiveFilterChips
                filters={table.filters.values}
                filterLabels={filterLabels}
                onRemove={handleRemoveFilter}
            />

            {/* Data Table */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader>
                    <DataTableControlBar table={table} />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            title={
                                table.filters.activeCount > 0
                                    ? "Data Tidak Ditemukan"
                                    : "Data Kosong"
                            }
                            description={
                                table.filters.activeCount > 0
                                    ? "Tidak ada purchase order yang cocok dengan filter."
                                    : "Belum ada data purchase order."
                            }
                        />
                    ) : (
                        <Table aria-label="Tabel Pembelian" shadow="none">
                            <TableHeader>
                                <TableColumn key="no" width={50}>
                                    No.
                                </TableColumn>
                                <TableColumn key="order_number">
                                    No. Order
                                </TableColumn>
                                <TableColumn key="supplier">
                                    Supplier
                                </TableColumn>
                                <TableColumn key="order">Tanggal</TableColumn>
                                <TableColumn key="total" align="end">
                                    Total
                                </TableColumn>
                                <TableColumn key="payment_status">
                                    Status Pembayaran
                                </TableColumn>
                                <TableColumn key="order_status">
                                    Status Order
                                </TableColumn>
                                <TableColumn align="end" key="actions">
                                    Aksi
                                </TableColumn>
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => (
                                    <TableRow key={item.id}>
                                        {/* No. */}
                                        <TableCell>
                                            <ThemedText
                                                size="sm"
                                                variant="secondary"
                                            >
                                                {table.items.indexOf(item) +
                                                    1 +
                                                    (table.pagination.page -
                                                        1) *
                                                        table.pagination
                                                            .perPage}
                                                .
                                            </ThemedText>
                                        </TableCell>
                                        {/* Order Number */}
                                        <TableCell>
                                            <ThemedText size="sm" className="font-medium">
                                                {item.order_number}
                                            </ThemedText>
                                        </TableCell>
                                        {/* Supplier */}
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {item.supplier?.name || "-"}
                                            </ThemedText>
                                        </TableCell>
                                        {/* Order Date & Due Date */}
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <ThemedText size="sm">
                                                    {formatDate(
                                                        item.order_date,
                                                    )}
                                                </ThemedText>
                                                <ThemedText
                                                    size="xs"
                                                    variant="secondary"
                                                >
                                                    {item.due_date
                                                        ? `Jatuh tempo: ${formatDate(item.due_date)}`
                                                        : "Tanpa jatuh tempo"}
                                                </ThemedText>
                                            </div>
                                        </TableCell>
                                        {/* Total & Payment Method */}
                                        <TableCell>
                                            <div className="flex flex-col items-end">
                                                <ThemedText
                                                    size="sm"
                                                    className="font-medium"
                                                >
                                                    {formatCurrency(
                                                        item.total_amount,
                                                    )}
                                                </ThemedText>
                                                <ThemedText
                                                    size="xs"
                                                    variant="secondary"
                                                >
                                                    {item.payment_method
                                                        ?.name || "-"}
                                                </ThemedText>
                                            </div>
                                        </TableCell>
                                        {/* Payment Status */}
                                        <TableCell>
                                            <Chip
                                                color={
                                                    item.payment_status
                                                        ?.badge_color ||
                                                    "default"
                                                }
                                                size="sm"
                                                className="font-medium"
                                                variant="flat"
                                            >
                                                {item.payment_status?.name ||
                                                    "Unknown"}
                                            </Chip>
                                        </TableCell>
                                        {/* Order Status */}
                                        <TableCell>
                                            <Chip
                                                color={
                                                    item.order_status
                                                        ?.badge_color ||
                                                    "default"
                                                }
                                                size="sm"
                                                className="font-medium"
                                                variant="flat"
                                            >
                                                {item.order_status?.name ||
                                                    "Unknown"}
                                            </Chip>
                                        </TableCell>
                                        {/* Actions */}
                                        <TableCell>
                                            <div className="relative flex items-center justify-end gap-2">
                                                <Tooltip content="Lihat Detail">
                                                    <Button
                                                        isIconOnly
                                                        size="sm"
                                                        variant="light"
                                                        as={Link}
                                                        to={`/purchase-order/${item.id}/detail`}
                                                    >
                                                        <LuEye
                                                            size={14}
                                                            className="text-default-500"
                                                        />
                                                    </Button>
                                                </Tooltip>
                                                {(() => {
                                                    const isFinalOrder = [4, 5].includes(item.order_status?.id);
                                                    const isPaid = [3, 4].includes(item.payment_status?.id);
                                                    const editLocked = isFinalOrder || (isPaid && !hasPermission("purchase-order.edit-paid"));
                                                    const deleteLocked = isFinalOrder || (isPaid && !hasPermission("purchase-order.delete-paid"));
                                                    const editLockReason = isFinalOrder
                                                        ? `Status sudah ${item.order_status?.name?.toLowerCase()}`
                                                        : `Pembayaran sudah ${item.payment_status?.name?.toLowerCase()}`;
                                                    const deleteLockReason = isFinalOrder
                                                        ? `Status sudah ${item.order_status?.name?.toLowerCase()}`
                                                        : `Pembayaran sudah ${item.payment_status?.name?.toLowerCase()}`;
                                                    return (
                                                        <>
                                                            {hasPermission("purchase-order.edit") && (
                                                                <Tooltip content={editLocked ? editLockReason : "Edit Order"}>
                                                                    {editLocked ? (
                                                                        <span><Button isIconOnly size="sm" variant="light" isDisabled className="opacity-30"><LuPencil size={14} /></Button></span>
                                                                    ) : (
                                                                        <Button isIconOnly size="sm" variant="light" as={Link} to={`/purchase-order/${item.id}/edit`}><LuPencil size={14} className="text-default-500" /></Button>
                                                                    )}
                                                                </Tooltip>
                                                            )}
                                                            {hasPermission("purchase-order.change-order-status") && (
                                                                <Tooltip content={isFinalOrder ? `Status sudah ${item.order_status?.name?.toLowerCase()}` : "Ubah Status Order"}>
                                                                    {isFinalOrder ? (
                                                                        <span><Button isIconOnly size="sm" variant="light" isDisabled className="opacity-30"><LuRefreshCw size={14} /></Button></span>
                                                                    ) : (
                                                                        <Button isIconOnly size="sm" variant="light" onPress={() => setOrderStatusModal({ isOpen: true, item })}><LuRefreshCw size={14} className="text-default-500" /></Button>
                                                                    )}
                                                                </Tooltip>
                                                            )}
                                                            {hasPermission("purchase-order.delete") && (
                                                                <Tooltip content={deleteLocked ? deleteLockReason : "Hapus Order"}>
                                                                    {deleteLocked ? (
                                                                        <span><Button isIconOnly size="sm" variant="light" isDisabled className="opacity-30"><LuTrash size={14} /></Button></span>
                                                                    ) : (
                                                                        <Button isIconOnly size="sm" color="danger" variant="light" onPress={() => table.deleteState.openDeleteModal(item.id)}><LuTrash size={14} /></Button>
                                                                    )}
                                                                </Tooltip>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>

            <DeleteConfirmModal table={table} />

            {/* Order Status Change Modal */}
            <ChangeStatusModal
                isOpen={orderStatusModal.isOpen}
                title="Ubah Status Order"
                onClose={() =>
                    setOrderStatusModal({ isOpen: false, item: null })
                }
                item={orderStatusModal.item}
                itemName={orderStatusModal.item?.order_number}
                statusEndpoint="select/order-status"
                statusField="order_status_id"
                endpoint="purchase-orders/{id}/change-order-status"
                onSuccess={() => table.refetch()}
                statusIdsRequiringConfirmation={[4]}
                confirmationMessage="Setelah status diubah ke 'Selesai', stok akan masuk ke gudang dan status tidak dapat diubah lagi."
            />
        </div>
    );
};

export default PurchaseOrderData;
